import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { FundRequest } from '../../types';
import apiService from '../../services/api';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  Button, 
  Badge,
  Input,
  Select,
  Pagination,
  PageButton,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableHeader
} from '../../styles/StyledComponents';
import IconWrapper from '../common/IconWrapper';
import { 
  FaSearch, 
  FaFilter, 
  FaEye, 
  FaCheckCircle, 
  FaTimes,
  FaPlus,
  FaDownload,
  FaFilePdf
} from 'react-icons/fa';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  h1 {
    color: var(--secondary-color);
    font-size: 1.8rem;
    margin: 0;
    
    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
    
    @media (max-width: 480px) {
      font-size: 1.3rem;
    }
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const FilterSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
  background-color: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    flex-direction: column;
    padding: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  label {
    font-weight: 500;
    color: var(--dark-gray);
    white-space: nowrap;
    font-size: 0.9rem;
    
    @media (max-width: 768px) {
      font-size: 0.85rem;
    }
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 300px;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 0.75rem;
  }
`;

const StatusBadge = styled(Badge)`
  min-width: 80px;
  text-align: center;
  font-size: 0.75rem;
  
  @media (max-width: 768px) {
    font-size: 0.7rem;
    min-width: 70px;
  }
`;

const AmountText = styled.span`
  font-weight: 700;
  color: var(--primary-color);
  font-size: 1rem;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const DateText = styled.span`
  color: var(--dark-gray);
  font-size: 0.85rem;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const RequestId = styled.div`
  font-weight: 700;
  color: var(--secondary-color);
  font-size: 0.95rem;
  margin-bottom: 0.25rem;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const ProjectName = styled.div`
  font-size: 0.85rem;
  color: var(--dark-gray);
  background: var(--light-gray);
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  display: inline-block;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const RequesterInfo = styled.div`
  font-weight: 600;
  color: var(--secondary-color);
  font-size: 0.9rem;
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const ApprovalDots = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  
  @media (max-width: 768px) {
    gap: 4px;
  }
`;

const Dot = styled.span<{ status: string }>`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${props => {
    switch (props.status) {
      case 'Approved': return '#28a745';
      case 'Rejected': return '#dc3545';
      case 'Pending': return '#6c757d';
      case 'INITIATED': return '#007bff';
      case 'PAID': return '#17a2b8';
      default: return '#6c757d';
    }
  }};
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.2);
  }
  
  @media (max-width: 768px) {
    width: 8px;
    height: 8px;
  }
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--dark-gray);
  
  .empty-icon {
    font-size: 4rem;
    color: var(--light-gray);
    margin-bottom: 1.5rem;
    
    @media (max-width: 768px) {
      font-size: 3rem;
    }
  }
  
  .empty-title {
    font-size: 1.3rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    
    @media (max-width: 768px) {
      font-size: 1.1rem;
    }
  }
  
  .empty-description {
    font-size: 1rem;
    line-height: 1.6;
    max-width: 400px;
    margin: 0 auto;
    
    @media (max-width: 768px) {
      font-size: 0.9rem;
      max-width: 300px;
    }
  }
`;

const ApprovalButton = styled(Button)<{ variant?: 'approve' | 'reject' }>`
  padding: 0.5rem 0.75rem;
  font-size: 0.85rem;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
  background: ${props => props.variant === 'reject' ? '#dc3545' : '#28a745'};
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px ${props => props.variant === 'reject' ? 'rgba(220, 53, 69, 0.3)' : 'rgba(40, 167, 69, 0.3)'};
  }
  
  @media (max-width: 768px) {
    padding: 0.4rem 0.6rem;
    font-size: 0.8rem;
  }
`;

const MobileCard = styled(Card)`
  margin-bottom: 1rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  
  .mobile-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1rem;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  
  .mobile-id {
    font-weight: 700;
    color: var(--secondary-color);
    font-size: 1rem;
  }
  
  .mobile-project {
    font-size: 0.85rem;
    color: var(--dark-gray);
    background: var(--light-gray);
    padding: 0.25rem 0.5rem;
    border-radius: 6px;
    display: inline-block;
  }
  
  .mobile-amount {
    font-weight: 700;
    color: var(--primary-color);
    font-size: 1.1rem;
  }
  
  .mobile-details {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 1rem;
    
    @media (max-width: 480px) {
      grid-template-columns: 1fr;
      gap: 0.75rem;
    }
  }
  
  .mobile-detail-item {
    .label {
      font-size: 0.8rem;
      color: var(--dark-gray);
      margin-bottom: 0.25rem;
    }
    
    .value {
      font-weight: 600;
      color: var(--secondary-color);
      font-size: 0.9rem;
    }
  }
  
  .mobile-actions {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    
    @media (max-width: 480px) {
      flex-direction: column;
    }
  }
`;

const DesktopView = styled.div`
  display: none;
  
  @media (min-width: 769px) {
    display: block;
  }
`;

const MobileView = styled.div`
  display: block;
  
  @media (min-width: 769px) {
    display: none;
  }
`;

const SupervisorFundRequestsList: React.FC = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState<FundRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<FundRequest[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [requestsPerPage] = useState(10);

  useEffect(() => {
    const fetchAssignedProjectRequests = async () => {
      try {
        // Get projects assigned to the current supervisor
        const assignedProjectsResponse = await apiService.getAssignedProjects();
        const assignedProjects = assignedProjectsResponse.data || [];
        
        // Backend now properly filters by role, so we can use the first project
        const supervisorProject = assignedProjects.length > 0 ? assignedProjects[0] : null;

        if (supervisorProject) {
          // Get fund requests for the assigned project
          const fundRequestsResponse = await apiService.getFundRequests({ 
            projectId: supervisorProject._id || supervisorProject.id 
          });
          const projectRequests = fundRequestsResponse.data || [];
          setRequests(projectRequests);
          setFilteredRequests(projectRequests);
        }
      } catch (error) {
        console.error("Error fetching assigned project requests:", error);
      }
    };

    if (user) {
      fetchAssignedProjectRequests();
    }
  }, [user]);

  useEffect(() => {
    let filtered = requests;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(request =>
        (request._id || request.id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.projectName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.requester.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(request => request.status === statusFilter);
    }

    setFilteredRequests(filtered);
    setCurrentPage(1);
  }, [searchTerm, statusFilter, requests]);

  // Get current requests for pagination
  const indexOfLastRequest = currentPage * requestsPerPage;
  const indexOfFirstRequest = indexOfLastRequest - requestsPerPage;
  const currentRequests = filteredRequests.slice(indexOfFirstRequest, indexOfLastRequest);

  // Change page
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const getStatusVariant = (status: string): 'success' | 'danger' | 'warning' | 'info' | 'primary' | 'secondary' => {
    switch (status) {
      case 'Approved': return 'success';
      case 'Rejected': return 'danger';
      case 'Under Review': return 'warning';
      case 'Paid': return 'info';
      case 'Draft': return 'primary';
      default: return 'secondary';
    }
  };

  const canApproveOrReject = (request: FundRequest): boolean => {
    const pendingStep = request.approvalChain.find(step => step.status === 'Pending');
    return pendingStep?.role === 'Supervisor';
  };

  const handleApprove = (requestId: string) => {
    setRequests(prevRequests => 
      prevRequests.map(request => {
        if ((request._id || request.id) === requestId) {
          return {
            ...request,
            status: 'Approved',
            approvalChain: request.approvalChain.map(step => 
              step.role === 'Supervisor' ? { ...step, status: 'Approved' } : step
            )
          };
        }
        return request;
      })
    );
  };

  const handleReject = (requestId: string) => {
    setRequests(prevRequests => 
      prevRequests.map(request => {
        if ((request._id || request.id) === requestId) {
          return {
            ...request,
            status: 'Rejected',
            approvalChain: request.approvalChain.map(step => 
              step.role === 'Supervisor' ? { ...step, status: 'Rejected' } : step
            )
          };
        }
        return request;
      })
    );
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (requests.length === 0) {
    return (
      <PageContainer>
        <PageHeader>
          <h1>Fund Requests</h1>
        </PageHeader>
        
        <NoDataMessage>
          <div className="empty-icon">📋</div>
          <div className="empty-title">No Fund Requests Found</div>
          <div className="empty-description">
            You are not assigned to any project or there are no fund requests for your assigned project.
          </div>
        </NoDataMessage>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <h1>Fund Requests</h1>
        <ActionButtons>
          <Button as={Link} to="/supervisor/funds/new">
            <IconWrapper icon={FaPlus} />
            New Request
          </Button>
          <Button>
            <IconWrapper icon={FaDownload} />
            Export Excel
          </Button>
          <Button>
            <IconWrapper icon={FaFilePdf} />
            Export PDF
          </Button>
        </ActionButtons>
      </PageHeader>

      <FilterSection>
        <SearchContainer>
          <Input
            type="text"
            placeholder="Search requests..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ paddingLeft: '2.5rem' }}
          />
          <IconWrapper 
            icon={FaSearch} 
            style={{ 
              position: 'absolute', 
              left: '0.75rem', 
              top: '50%', 
              transform: 'translateY(-50%)',
              color: 'var(--medium-gray)'
            }} 
          />
        </SearchContainer>

        <FilterGroup>
          <label>Status:</label>
          <Select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ minWidth: '120px' }}
          >
            <option value="all">All Status</option>
            <option value="Draft">Draft</option>
            <option value="Under Review">Under Review</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
            <option value="Paid">Paid</option>
          </Select>
        </FilterGroup>
      </FilterSection>

      {filteredRequests.length === 0 ? (
        <NoDataMessage>
          <div className="empty-icon">🔍</div>
          <div className="empty-title">No Requests Found</div>
          <div className="empty-description">
            No fund requests match your current search criteria.
          </div>
        </NoDataMessage>
      ) : (
        <>
          {/* Desktop Table View */}
          <DesktopView>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>Request ID</TableHeader>
                    <TableHeader>Project</TableHeader>
                    <TableHeader>Requester</TableHeader>
                    <TableHeader>Amount</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Created</TableHeader>
                    <TableHeader>Approval Chain</TableHeader>
                    <TableHeader>Actions</TableHeader>
                  </TableRow>
                </TableHead>
                <tbody>
                  {currentRequests.map((request) => (
                    <TableRow key={request.id}>
                      <TableCell>
                        <RequestId>{request.id}</RequestId>
                        <ProjectName>{request.projectName}</ProjectName>
                      </TableCell>
                      <TableCell>
                        <div>{request.projectName}</div>
                        <DateText>{request.timeline}</DateText>
                      </TableCell>
                      <TableCell>
                        <RequesterInfo>{request.requester}</RequesterInfo>
                        <DateText>{request.timeline}</DateText>
                      </TableCell>
                      <TableCell>
                        <AmountText>{formatAmount(request.amount, request.currency)}</AmountText>
                      </TableCell>
                      <TableCell>
                        <StatusBadge variant={getStatusVariant(request.status)}>
                          {request.status}
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        <DateText>{formatDate(request.createdAt)}</DateText>
                      </TableCell>
                      <TableCell>
                        <ApprovalDots>
                          {request.approvalChain.map((step, index) => (
                            <Dot key={index} status={step.status} title={`${step.role}: ${step.status}`} />
                          ))}
                        </ApprovalDots>
                      </TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <Button as={Link} to={`/supervisor/funds/${request.id}`} style={{ padding: '0.4rem 0.6rem', fontSize: '0.8rem' }}>
                            <IconWrapper icon={FaEye} />
                            View
                          </Button>
                          {canApproveOrReject(request) && (
                            <>
                              <ApprovalButton 
                                variant="approve"
                                onClick={() => handleApprove(request._id || request.id || '')}
                              >
                                <IconWrapper icon={FaCheckCircle} />
                                Approve
                              </ApprovalButton>
                              <ApprovalButton 
                                variant="reject"
                                onClick={() => handleReject(request._id || request.id || '')}
                              >
                                <IconWrapper icon={FaTimes} />
                                Reject
                              </ApprovalButton>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </TableContainer>
          </DesktopView>

          {/* Mobile Card View */}
          <MobileView>
            {currentRequests.map((request) => (
              <MobileCard key={request.id}>
                <div className="mobile-header">
                  <div>
                    <div className="mobile-id">{request.id}</div>
                    <div className="mobile-project">{request.projectName}</div>
                  </div>
                  <div className="mobile-amount">{formatAmount(request.amount, request.currency)}</div>
                </div>

                <div className="mobile-details">
                  <div className="mobile-detail-item">
                    <div className="label">Requester</div>
                    <div className="value">{request.requester}</div>
                  </div>
                  <div className="mobile-detail-item">
                    <div className="label">Timeline</div>
                    <div className="value">{request.timeline}</div>
                  </div>
                  <div className="mobile-detail-item">
                    <div className="label">Status</div>
                    <div className="value">
                      <StatusBadge variant={getStatusVariant(request.status)}>
                        {request.status}
                      </StatusBadge>
                    </div>
                  </div>
                  <div className="mobile-detail-item">
                    <div className="label">Created</div>
                    <div className="value">{formatDate(request.createdAt)}</div>
                  </div>
                </div>

                <div className="mobile-actions">
                  <Button as={Link} to={`/supervisor/funds/${request.id}`} style={{ flex: 1 }}>
                    <IconWrapper icon={FaEye} />
                    View Details
                  </Button>
                  {canApproveOrReject(request) && (
                    <>
                      <ApprovalButton 
                        variant="approve"
                        onClick={() => handleApprove(request._id || request.id || '')}
                        style={{ flex: 1 }}
                      >
                        <IconWrapper icon={FaCheckCircle} />
                        Approve
                      </ApprovalButton>
                      <ApprovalButton 
                        variant="reject"
                        onClick={() => handleReject(request._id || request.id || '')}
                        style={{ flex: 1 }}
                      >
                        <IconWrapper icon={FaTimes} />
                        Reject
                      </ApprovalButton>
                    </>
                  )}
                </div>
              </MobileCard>
            ))}
          </MobileView>

          {filteredRequests.length > requestsPerPage && (
            <Pagination>
              <PageButton 
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </PageButton>
              
              {Array.from({ length: Math.ceil(filteredRequests.length / requestsPerPage) }, (_, i) => (
                <PageButton
                  key={i + 1}
                  onClick={() => paginate(i + 1)}
                  active={currentPage === i + 1}
                >
                  {i + 1}
                </PageButton>
              ))}
              
              <PageButton 
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === Math.ceil(filteredRequests.length / requestsPerPage)}
              >
                Next
              </PageButton>
            </Pagination>
          )}
        </>
      )}
    </PageContainer>
  );
};

export default SupervisorFundRequestsList; 